import React, { useMemo, useState } from "react";
import { X, Lock, Check, Sparkles, Hourglass, TrendingUp } from "lucide-react";
import { Flag, fmtFans } from "../../lib/tour";
import { TourCurrencyStrip } from "./TourCurrencyStrip";
import { ScreenPortal } from "../MobileZoom";

export function PurchaseBurst({ color = "#39FF14" }) {
  const particles = useMemo(() => Array.from({ length: 22 }).map((_, i) => {
    const a = (i / 22) * Math.PI * 2;
    const d = 70 + Math.random() * 60;
    return { id: i, dx: Math.cos(a) * d, dy: Math.sin(a) * d, s: 3 + Math.random() * 5, delay: Math.random() * 60 };
  }), []);
  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" aria-hidden="true" data-testid="tour-upgrade-burst">
      <div className="absolute inset-0" style={{ background: `radial-gradient(circle at center, ${color}55 0%, transparent 65%)`, animation: "shop-flash 0.5s ease-out forwards" }} />
      <div className="absolute w-6 h-6 rounded-full" style={{ border: `2px solid ${color}`, boxShadow: `0 0 20px ${color}`, animation: "shop-ring 0.8s ease-out forwards" }} />
      {particles.map((p) => (
        <span key={p.id} className="absolute rounded-full" style={{
          width: p.s, height: p.s, background: color, boxShadow: `0 0 10px ${color}`,
          animation: `shop-particle 1.0s ${p.delay}ms cubic-bezier(.2,.7,.3,1) forwards`, "--dx": `${p.dx}px`, "--dy": `${p.dy}px`,
        }} />
      ))}
      <div className="absolute font-heading text-2xl font-black tracking-widest" style={{ color, textShadow: `0 0 16px ${color}, 0 0 32px ${color}`, animation: "shop-banner 1.0s ease-out forwards" }}>
        UPGRADED!
      </div>
    </div>
  );
}

function LockedCard({ u }) {
  return (
    <div data-testid={`tour-upgrade-${u.id}`} data-locked="true"
         className="rounded-xl p-4 border-2 border-dashed border-[#2D1C52] bg-[#0A051A]/80 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-[#1C0D40] flex items-center justify-center shrink-0"><Lock size={18} className="text-[#695D8E]" /></div>
      <div>
        <div className="font-heading uppercase tracking-widest text-[#695D8E]">??? Locked Upgrade</div>
        <div className="font-mono text-[11px] text-[#A89BCC]" data-testid={`tour-upgrade-needed-${u.id}`}>
          Unlocks after <span className="text-[#FFE600]">{u.purchases_needed}</span> more purchase{u.purchases_needed === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

function UpgradeCard({ u, onBuy, busy }) {
  const [burst, setBurst] = useState(0);
  if (!u.unlocked) return <LockedCard u={u} />;
  const buy = async () => {
    if (await onBuy(u.id)) {
      setBurst((b) => b + 1);
      setTimeout(() => setBurst(0), 1200);
    }
  };
  return (
    <div data-testid={`tour-upgrade-${u.id}`} data-locked="false"
         className={`relative overflow-hidden rounded-xl p-4 border-2 bg-gradient-to-br from-[#1C0D40] to-[#0A051A] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 ${burst ? "scale-[1.03]" : ""} ${u.maxed ? "border-[#FFD700]/60" : u.affordable ? "border-[#39FF14]/70 shadow-[0_0_16px_#39FF1433]" : "border-[#00F0FF]/40"}`}>
      {burst > 0 && <PurchaseBurst key={burst} color={u.maxed ? "#FFD700" : "#39FF14"} />}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading text-lg uppercase tracking-wider text-white leading-tight">{u.name}</div>
          <div className="font-mono text-[11px] text-[#00F0FF] mt-0.5" data-testid={`tour-upgrade-effect-${u.id}`}>{u.effect}</div>
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-md font-mono text-[9px] uppercase tracking-widest border ${u.maxed ? "text-[#FFD700] border-[#FFD700]" : u.affordable ? "text-[#39FF14] border-[#39FF14]" : "text-[#FF9500] border-[#FF9500]"}`}>
          {u.maxed ? "Owned" : u.requires_missing ? "Needs Airport" : u.affordable ? "Available" : "Need fans"}
        </span>
      </div>
      {u.total && (
        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/40 font-mono text-[10px] text-[#39FF14]" data-testid={`tour-upgrade-total-${u.id}`}>
          <TrendingUp size={11} /> Current: {u.total}
        </div>
      )}
      <div className="flex items-center justify-between mt-3 gap-2">
        <div className="font-mono text-[11px] text-[#A89BCC]" data-testid={`tour-upgrade-owned-${u.id}`}>
          Purchased <span className="text-white font-bold">{u.owned}{u.max ? ` / ${u.max}` : ""}</span>
        </div>
        {u.maxed ? (
          <div className="flex items-center gap-1 font-heading text-sm text-[#FFD700]"><Check size={16} /> Complete</div>
        ) : (
          <button data-testid={`tour-upgrade-buy-${u.id}`} disabled={!u.affordable || busy || u.requires_missing} onClick={buy}
                  className="btn-neon text-xs px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-40 active:scale-95 transition-transform">
            <Flag c={u.currency} className="w-4 h-3" /> {fmtFans(u.cost)}
          </button>
        )}
      </div>
    </div>
  );
}

export function TourUpgradesModal({ state, onClose, onBuy, busy }) {
  return (<ScreenPortal>
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="tour-upgrades-modal"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card-surface w-full max-w-3xl max-h-[88vh] flex flex-col border-2 border-[#FFE600]/60 shadow-[0_0_40px_#FFE60033] pw-pop overflow-hidden">
        <div className="p-5 pb-3 border-b border-[#2D1C52] bg-[#0A051A]/60">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles size={22} className="text-[#FFE600]" />
            <h2 className="font-heading text-3xl uppercase neon-yellow">Tour Upgrades</h2>
            <span className="font-mono text-[11px] text-[#A89BCC]" data-testid="tour-upgrades-total">
              <span className="text-[#FFE600] font-bold">{state.total_purchases}</span> purchases
            </span>
            <button data-testid="tour-upgrades-close" onClick={onClose} className="ml-auto p-2 rounded-lg hover:bg-white/10"><X size={18} /></button>
          </div>
          <TourCurrencyStrip fans={state.fans} handshakes={state.handshakes} prefix="tour-upg" />
        </div>
        <div className="p-5 overflow-y-auto tour-scroll">
          <div className="grid sm:grid-cols-2 gap-3">
            {state.upgrades.map((u) => <UpgradeCard key={u.id} u={u} onBuy={onBuy} busy={busy} />)}
            <div className="rounded-xl p-4 border-2 border-dashed border-[#9D00FF]/40 flex items-center gap-3 text-[#9D00FF]" data-testid="tour-upgrade-coming-soon">
              <Hourglass size={18} /> <span className="font-heading uppercase tracking-widest">More coming soon…</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ScreenPortal>);
}
